import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 p-4">
          <div className="bg-black/40 backdrop-blur-sm rounded-lg p-8 max-w-lg w-full border border-blue-500/20">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-8 w-8 text-red-400" />
              <h1 className="text-xl font-semibold text-white">Something went wrong</h1>
            </div>
            <p className="text-gray-300 mb-6">
              We're sorry, but something went wrong. Please try refreshing the page or contact support if the problem persists.
            </p>
            <pre className="bg-black/20 p-4 rounded text-sm text-gray-300 overflow-x-auto">
              {this.state.error?.message}
            </pre>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-md transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
